import React, { useState } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import ModalEnquiry from './ModalEnquiry';
import { motion } from 'framer-motion';

const learnCards = [
  {
    title: 'Foundations',
    points: [
      'System thinking',
      'Prompt engineering basics',
      'AI-first design principles',
      'Vibe coding mindset',
    ],
  },
  {
    title: 'Front-End Engineering',
    points: [
      'React & Tailwind CSS',
      'UI/UX for AI apps',
      'Prompt-driven interfaces',
      'Modern web patterns',
    ],
  },
  {
    title: 'Back-End Engineering',
    points: [
      'Node.js / Python APIs',
      'Database design',
      'AI integration',
      'Secure, scalable systems',
    ],
  },
  {
    title: 'Full-Stack E2E Solutions',
    points: [
      'Ideate to deploy',
      'E2E project workflow',
      'Testing & automation',
      'Real-world delivery',
    ],
  },
];

const aiTools = [
  { label: 'Design with AI', icon: '🎨' },
  { label: 'Code with AI', icon: '💻' },
  { label: 'Test with AI', icon: '🧪' },
  { label: 'Deploy with AI', icon: '🚀' },
];

export default function TrainingsPage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <div style={{ backgroundColor: 'var(--bg)' }} className="min-h-screen w-full overflow-x-hidden">
      <Navbar />

      <section className="pt-32 pb-16 px-6 md:px-8 flex flex-col items-center text-center">
        <div className="eyebrow mb-4">Trainings</div>
        <motion.h1
          className="t-h1 font-display mb-6 max-w-3xl"
          style={{ color: 'var(--text-primary)' }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          Become the Architect of the Future
        </motion.h1>
        <motion.p
          className="t-body max-w-2xl"
          style={{ color: 'var(--text-secondary)' }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          We don't just teach code. We train minds to build intelligent systems using prompt
          engineering, vibe coding, and AI-first design.
        </motion.p>
      </section>

      <section className="py-14 px-6 md:px-8 flex flex-col items-center" style={{ backgroundColor: 'var(--bg-subtle)' }}>
        <div className="container-premium flex flex-col items-center text-center">
          <h2 className="t-h2 mb-4" style={{ color: 'var(--text-primary)' }}>
            AI Coders Will Shape the Next Era
          </h2>
          <p className="t-body max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
            Traditional coding is shifting. Vibe coding, AI collaboration, and system thinking are
            the new superpowers. At Ufeus, you'll learn to build with and for AI, not just code for
            machines.
          </p>
        </div>
      </section>

      <section className="py-20 px-6 md:px-8">
        <div className="container-premium">
          <h2 className="t-h1 font-display mb-12 text-center" style={{ color: 'var(--text-primary)' }}>
            What You'll Learn
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {learnCards.map((card, i) => (
              <motion.div
                key={card.title}
                className="card p-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <h3 className="t-h3 mb-3" style={{ color: 'var(--accent)' }}>{card.title}</h3>
                <ul className="beyond-list space-y-1">
                  {card.points.map((pt) => (
                    <li key={pt}>{pt}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 md:px-8" style={{ backgroundColor: 'var(--bg-subtle)' }}>
        <div className="container-premium flex flex-col items-center">
          <h2 className="t-h1 font-display mb-10 text-center" style={{ color: 'var(--text-primary)' }}>
            AI Tools &amp; Prompt Engineering
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 w-full max-w-3xl">
            {aiTools.map((tool, i) => (
              <motion.div
                key={tool.label}
                className="tool-card card flex flex-col items-center justify-center py-8 px-2"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <span className="text-3xl mb-3">{tool.icon}</span>
                <span className="t-sm text-center" style={{ color: 'var(--text-primary)' }}>{tool.label}</span>
              </motion.div>
            ))}
          </div>
          <p className="t-body max-w-2xl text-center" style={{ color: 'var(--text-secondary)' }}>
            You'll learn to use these tools to build full projects with minimal code, leveraging AI
            to accelerate every step.
          </p>
        </div>
      </section>

      <section className="py-20 px-6 md:px-8">
        <div className="container-premium flex flex-col items-center text-center">
          <h2 className="t-h1 font-display mb-6" style={{ color: 'var(--text-primary)' }}>
            Implementation Phase
          </h2>
          <p className="t-body max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
            Students build an end-to-end real-world project — from ideation through design, build,
            test, and deploy.
          </p>
        </div>
      </section>

      <section className="py-20 px-6 md:px-8" style={{ backgroundColor: 'var(--bg-subtle)' }}>
        <div className="container-premium flex flex-col items-center text-center">
          <h2 className="t-h1 font-display mb-6" style={{ color: 'var(--text-primary)' }}>
            What Comes After?
          </h2>
          <p className="t-body max-w-2xl mb-8" style={{ color: 'var(--text-secondary)' }}>
            If the final project is successful, students get a chance to join Ufeus as a paid team
            member and build commercial products.
          </p>
          <blockquote
            className="t-h3 font-normal italic max-w-2xl"
            style={{ color: 'var(--accent)', borderLeft: '3px solid var(--accent)', paddingLeft: '1.5rem' }}
          >
            "You're not just learning to code. You're learning to build the future — without the noise."
          </blockquote>
        </div>
      </section>

      <button
        className="fab-ring fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full px-5 py-3 shadow-lg t-sm font-medium"
        style={{ backgroundColor: 'var(--accent)', color: '#17130a' }}
        onClick={() => setShowModal(true)}
      >
        <span className="dot-pulse" style={{ backgroundColor: '#17130a' }} />
        Enquire Now
      </button>

      <ModalEnquiry open={showModal} onClose={() => setShowModal(false)} triggerLabel="Enquire About Trainings" />

      <Footer />
    </div>
  );
}
